import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EventPartnerLayout } from "@/components/eventPartner/EventPartnerLayout";
import { EventCard } from "@/components/eventPartner/EventCard";
import { AggregateStatsCards } from "@/components/eventPartner/AggregateStatsCards";
import { TripStatisticsCard } from "@/components/eventPartner/TripStatisticsCard";
import { VehicleTypeBreakdown } from "@/components/eventPartner/VehicleTypeBreakdown";
import { EventTripsTable } from "@/components/eventPartner/EventTripsTable";
import { EventVehiclePricing } from "@/components/eventPartner/EventVehiclePricing";
import {
  getManagedEvents,
  getAllEventTrips,
  calculateTripStatistics,
  getEventPartnerToken,
} from "@/services/eventPartnerService";
import { API_URL } from "@/config/api";
import type { CommunityEvent, TripStatistics, BusPricingOption } from "@/types/eventPartner";

const EventPartnerDashboard = () => {
  const [selectedEvent, setSelectedEvent] = useState<CommunityEvent | null>(null);

  // Debug: Fetch trips endpoint to see if it includes manager info
  useEffect(() => {
    const debugFetch = async () => {
      const token = getEventPartnerToken();
      if (!token) return;
      
      try {
        // Fetch trips for the specific event
        const tripsResponse = await fetch(
          `${API_URL}trips/event/d42a1807-432b-4dc9-877f-1a06d72b0794/`,
          {
            headers: {
              Authorization: `Token ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const tripsData = await tripsResponse.json();
        console.log("=== DEBUG: Trips endpoint response ===");
        console.log("Total trips:", tripsData.count);
        if (tripsData.results?.length > 0) {
          console.log("First trip community_event:", tripsData.results[0].community_event);
          console.log("First trip full data:", JSON.stringify(tripsData.results[0], null, 2));
        }
        console.log("Full trips response:", JSON.stringify(tripsData, null, 2));
      } catch (err) {
        console.error("Debug fetch error:", err);
      }
    };
    debugFetch();
  }, []);

  // Fetch managed events
  const {
    data: eventsData,
    isLoading: eventsLoading,
    error: eventsError,
  } = useQuery({
    queryKey: ["managed-events"],
    queryFn: getManagedEvents,
  });

  // Fetch trips for selected event
  const {
    data: tripsData,
    isLoading: tripsLoading,
  } = useQuery({
    queryKey: ["event-trips", selectedEvent?.id],
    queryFn: () => getAllEventTrips(selectedEvent!.id),
    enabled: !!selectedEvent,
  });

  // Calculate statistics
  const statistics: TripStatistics | null = tripsData
    ? calculateTripStatistics(tripsData)
    : null;

  // Get bus pricing info from selectedEvent (primary source) or from trips as fallback
  const busPricingInfo: Record<string, BusPricingOption> | null = 
    selectedEvent?.bus_pricing_info ?? 
    (tripsData && tripsData.length > 0 && tripsData[0].community_event?.bus_pricing_info
      ? tripsData[0].community_event.bus_pricing_info
      : null);

  const handleSelectEvent = (event: CommunityEvent) => {
    setSelectedEvent(event);
  };

  const handleBackToEvents = () => {
    setSelectedEvent(null);
  };

  return (
    <EventPartnerLayout>
      {!selectedEvent ? (
        // Events List View
        <div className="space-y-6">
          {/* Aggregate Stats */}
          {eventsData?.results && eventsData.results.length > 0 && (
            <AggregateStatsCards events={eventsData.results} />
          )}

          <div>
            <h1 className="text-2xl font-bold text-gray-900">Your Events</h1>
            <p className="text-gray-500 mt-1">
              Select an event to view trip details and statistics
            </p>
          </div>

          {eventsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-48 rounded-lg" />
              ))}
            </div>
          ) : eventsError ? (
            <div className="text-center py-12">
              <p className="text-destructive mb-4">
                Failed to load events. Please try again.
              </p>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </div>
          ) : eventsData?.results.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h2 className="text-lg font-medium text-gray-900 mb-2">
                No Events Found
              </h2>
              <p className="text-gray-500">
                You don't have any events assigned to your account yet.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {eventsData?.results.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  onClick={() => handleSelectEvent(event)}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        // Event Detail View
        <div className="space-y-6">
          {/* Header with back button */}
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackToEvents}
              className="flex-shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {selectedEvent.name}
              </h1>
              <p className="text-gray-500">
                {selectedEvent.venue_name}
              </p>
            </div>
          </div>

          {/* Statistics */}
          {statistics && (
            <TripStatisticsCard
              statistics={statistics}
              isLoading={tripsLoading}
            />
          )}

          {/* Vehicle Pricing Options */}
          <EventVehiclePricing
            busPricingInfo={busPricingInfo}
            isLoading={tripsLoading}
          />

          {/* Vehicle Breakdown */}
          <VehicleTypeBreakdown
            breakdown={statistics?.byVehicleType || []}
            busPricingInfo={busPricingInfo}
            isLoading={tripsLoading}
          />

          {/* Trips Table */}
          <EventTripsTable
            trips={tripsData || []}
            isLoading={tripsLoading}
          />
        </div>
      )}
    </EventPartnerLayout>
  );
};

export default EventPartnerDashboard;
